import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { fetchCarInventory } from '../services/api';

// Создаем контекст для управления данными автомобилей
const CarsContext = createContext(null);

/**
 * Provider для управления состоянием списка автомобилей
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Дочерние компоненты
 * @param {boolean} [props.autoLoad=true] - Автоматически загружать каталог при монтировании
 *   Установите false для lazy loading (каталог загрузится только при вызове loadCars())
 */
export const CarsProvider = ({ children, autoLoad = true }) => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(autoLoad); // Если autoLoad=false, не показываем loading сразу
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Ref для предотвращения race conditions при быстрых последовательных вызовах
  const loadingRef = useRef(false);

  // Функция для загрузки каталога (может быть вызвана вручную)
  const loadCars = useCallback(async () => {
    // Предотвращаем повторную загрузку (используем ref для актуального значения)
    if (isLoaded || loadingRef.current) {
      console.log('[CarsContext] Skipping load - already loaded or loading');
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      setError(null);
      console.log('[CarsContext] Loading car inventory...');
      const data = await fetchCarInventory();
      setCars(data);
      setIsLoaded(true);
      console.log('[CarsContext] Loaded', data.length, 'cars');
    } catch (err) {
      setError(err.message);
      console.error('[CarsContext] Failed to load cars:', err);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [isLoaded]); // Убрали loading из dependencies, используем loadingRef

  // Автозагрузка только если autoLoad=true
  useEffect(() => {
    if (autoLoad && !isLoaded) {
      loadCars();
    }
  }, [autoLoad, isLoaded, loadCars]);

  // Вспомогательные функции для работы с данными
  const getHotOffers = () => cars.filter(car => car.isHotOffer);
  const getCarById = (id) => cars.find(car => car.id === String(id));
  const getAllCars = () => cars;

  const value = {
    cars,
    loading,
    error,
    isLoaded,
    loadCars, // Экспортируем для ручной загрузки
    getHotOffers,
    getCarById,
    getAllCars,
  };

  return <CarsContext.Provider value={value}>{children}</CarsContext.Provider>;
};

/**
 * Hook для использования контекста автомобилей
 */
export const useCars = () => {
  const context = useContext(CarsContext);
  if (!context) {
    throw new Error('useCars must be used within CarsProvider');
  }
  return context;
};
